import React, { useState, useMemo, useEffect, useCallback} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CustomTablePagination from 'src/shared/TablePagination/CustomTablePagination';
import { GridContainer, Grid } from 'src/shared/grid';
import Autocomplete from '@mui/material/Autocomplete';
import { Box, Button, Modal, TextField, Tooltip } from '@mui/material';
import { ValidCharacters, validateCharacters,ONEDAY, isValidDate, errorResponse} from 'src/store/common';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment-timezone';
import UploadIcon from '@mui/icons-material/Upload';
import {DataUpload} from './DataUpload';
import './DataImport.scss';
import { StyledTableCell, StyledTableRow, modalUpload } from 'src/shared/Styles/Styles';
import { useStoreContext } from 'src/store/context';
import { useAddAlert, useUser } from 'src/store/actions';
import { getBulkDataFileNames, getBulkJobs, getCodes } from 'src/apis/getFuelSampleCalls';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoadingSpinner from 'src/shared/loading/loading';
import { alertMessages } from 'src/constants';


interface Data {
  jobId: number;
  fileName: string;
  createdTime: string;
  jobCompletionTime: string;
  jobType: string;
  size: string;
  jobStatus: string;
  createdBy: string;
  originalFileLink: string;
  reportLink: string;
}

function createData(
  jobId: number,
  fileName: string,
  createdTime: string,
  jobCompletionTime: string,
  jobType: string,
  size: string,
  jobStatus:string,
  createdBy: string,
  originalFileLink: string,
  reportLink: string
): Data {
  return {
    jobId,
    fileName,
    createdTime,
    jobCompletionTime,
    size,
    jobType,
    jobStatus,
    createdBy,
    originalFileLink: originalFileLink,
    reportLink: reportLink
  };
}

type Order = 'asc' | 'desc';

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'fileName',
    numeric: false,
    disablePadding: true,
    label: 'FILE NAME',
  },
  {
    id: 'createdTime',
    numeric: false,
    disablePadding: false,
    label: 'UPLOADED DATE',
  },
  {
    id: 'jobCompletionTime',
    numeric: true,
    disablePadding: false,
    label: 'PROCESSED DATE',
  },
  {
    id: 'jobType',
    numeric: true,
    disablePadding: false,
    label: 'DATA TYPE',
  },
  {
    id: 'size',
    numeric: true,
    disablePadding: false,
    label: 'SIZE',
  },
  {
    id: 'jobStatus',
    numeric: true,
    disablePadding: false,
    label: 'STATUS',
  },
  {
    id: 'createdBy',
    numeric: true,
    disablePadding: false,
    label: 'UPLOADED BY',
  },
];

interface EnhancedTableProps {
  order: Order;
  orderBy: string;
}

/*SPECIAL SORTABLE HEADER*/
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy } = props;

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            className="text-align-override headerParent"
            key={headCell.id}
            align={'left'}
            tabIndex={0}
            sortDirection={orderBy === headCell.id ? order : false}
          >
              {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

type DataImportProps={
  setFuelValuesString?: any
}

export const DataImport: React.FC<DataImportProps>= ({setFuelValuesString}) => {
  const [state, dispatch] = useStoreContext();
  const setUser = useUser(dispatch);
  const [token, setToken] = useState<string|null>(null);
  const [order] = useState<Order>('asc');
  const [orderBy] = useState<keyof Data>('fileName');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const addAlert = useAddAlert(dispatch);

  const allCodeObj =  useMemo(()=>({code_key: "All",code_table_id: -1, code_value:null, type:'All'}),[]);

  const [fileStatuses, setFileStatuses] = useState<any[]>([allCodeObj]);
  const [status, setStatus] = useState<string|undefined>(fileStatuses[0].code_key);
  const [statusInput, setStatusInput] = useState('');

  const [dataTypes, setDataTypes] = useState<any[]>([allCodeObj]);
  const [dataType, setDataType] = useState<string|undefined>(dataTypes[0].code_key);
  const [inputDataType, setInputDataType] = useState('');
  
  const [fileFormats, setFileFormats] = useState<any[]>([allCodeObj]);
  const [format, setFormat] = useState<string|undefined>(fileFormats[0].code_key);
  const [inputFormat, setInputFormat] = useState('');

  const [fileNames, setFileNames] = useState<any[]>([]);
  const [fileNameSearch, setFileNameSearch] = useState<string|null>(null);
  const [files, setFiles] = useState<Data[]>([]);

  const [startDate, setStartDate] = useState<any>(moment(moment().valueOf()-(30*ONEDAY)).set({hour:0,minute:0,second:0,millisecond:0}).utc().toDate());
  const [endDate, setEndDate] = useState<any>(moment().set({hour:23,minute:59,second:59,millisecond:0}).utc().toDate());
  const [errorStartDate, setErrorStartDate] = useState<string|null>(null);
  const [errorEndDate, setErrorEndDate] = useState<string|null>(null);

  const [openUploadModal, setOpenUploadModal] = useState(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [callMadeCodes, setCallMadeCodes] = useState(false);

  const handleOpenUpload = () => setOpenUploadModal(true);
  const handleCloseUpload= (event, reason) => {
    setOpenUploadModal(false);
  }
  const generateDownloadLink = (jobId, jobStatus) => {
    const report = jobStatus.indexOf('Complete')>-1 ? true : false;
    const fuelDownloadURL = window.location.host.includes('localhost') ? 
      `${process.env.REACT_APP_FIELD_SAMPLE_API_URL}`.split('apis/graphql', 1)[0].concat('fuelmodel/bulkUpdate/download') :
      `${process.env.REACT_APP_GRAPHQL_URL}`.split('api/climatology/graphql', 1)[0].concat('fuelmodel/bulkUpdate/download');
    const originalFile = `${fuelDownloadURL}?jobId=${jobId}&report=false`;
    const reportFile = `${fuelDownloadURL}?jobId=${jobId}&report=${report}`;
    return [originalFile, reportFile];
  }

  const fileNamesResponse = useCallback(() => {
    (async () => {
      try {
        const response: any = await getBulkDataFileNames();
        setFileNames(response);
      } catch (error) {
        errorResponse(error, addAlert, alertMessages.getFiles);
      }
    })();
  }, [addAlert]);

  /**Make a call to populate the table with the bulk data job files */
  const bulkDataResponse = useCallback((request: any) => {
    (async () => {
      try {
        setLoading(true);
        const bulkDataResponse: any = await getBulkJobs(request);
        const files = bulkDataResponse.files;
        const pageInfo = bulkDataResponse.pageInfo;
        setTotalCount(pageInfo.total_count);
        if(files?.length > 0) {
          let dataArray:Data[] = [];
          files.forEach((file:any) => {
            const [originalFile, reportFile] = generateDownloadLink(file.bulkDataJobId, file.jobStatus);
            dataArray.push(createData(
              file.bulkDataJobId,
              file.fileName,
              moment.utc(file.createdTime).utcOffset(state.userTimeZone.offset).format('MM/DD/YYYY'),
              file.completionTime ? moment.utc(file.completionTime).utcOffset(state.userTimeZone.offset).format('MM/DD/YYYY') : '',
              file.jobType,
              file.fileSize,
              file.jobStatus,
              file.createdBy,
              originalFile, 
              reportFile
            ));
          });
          setFiles(dataArray);
        } else {
          setFiles([]);
        }
      } catch (error) {
        setFiles([]);
        errorResponse(error, addAlert, alertMessages.getFiles);
        console.log('error', error);
      }
      setLoading(false);
    })();
  }, [state.userTimeZone.offset, addAlert]); 

  const handleRefresh = useCallback(() => {
    const request = {
      returnAll: false,
      status: status?.toUpperCase(),
      fileName:  fileNameSearch,
      uploadStartDate: startDate,
      uploadEndDate: endDate,
      perPage:  rowsPerPage,
      page: page,
    }
    bulkDataResponse(request);
    fileNamesResponse();
  },[status, fileNameSearch, startDate, endDate, rowsPerPage, page, bulkDataResponse, fileNamesResponse]);

  useEffect(()=>{
    /** find access token and set to send to bearer */
    if(state.userTokens && token) {
      /** If the Token is refreshed this will set the @param token equal to updated value */
      setToken(state.userTokens.accessToken);
    }
    else if(localStorage.getItem('user') && state.userTokens && !token){
      /** find access token and set to send to bearer */
      const parsedSession = JSON.parse(localStorage.getItem('user')??'');
      setUser(parsedSession?.userInfo);
      setToken(state.userTokens.accessToken);
    }
    else if(!state.user){
      const parsedSession = JSON.parse(localStorage.getItem('user')??'')
      setUser(parsedSession?.userInfo)    
    }
  },[state.userTokens, setUser, setToken, state.user, token]);

  /**Populate the File Search Field */
  useEffect(() => {
    if(fileNames && fileNames?.length < 1) {
      fileNamesResponse();
    }
  }, [addAlert, fileNamesResponse, fileNames]);

  /**Populate the table */
  useEffect(() => {
    const request = {
      returnAll: false,
      status: status?.toUpperCase(),
      dataType: dataType?.toUpperCase(),
      fileName:  fileNameSearch,
      uploadStartDate: startDate,
      uploadEndDate: endDate,
      perPage:  rowsPerPage,
      page: page,
    }
    bulkDataResponse(request);
  },[bulkDataResponse, fileNameSearch, dataType, status, startDate, endDate, page, rowsPerPage]);

  /**Auto refresh the table at a set interval of 45000 Miliseconds */
  useEffect(() => {
    const autoRefreshTable = setInterval(() => {
      handleRefresh();
    }, 45000);
    return () => {
      if(autoRefreshTable){
        clearInterval(autoRefreshTable);
      }
    }
  },[handleRefresh]);
  
  /**Make a call to populate the codes for the filters */
  useEffect(() => {
    if (!callMadeCodes && fileStatuses?.length < 2) {
      setCallMadeCodes(true);
      (async () => {
        try {
          const requestVars ={
            returnAll: true,
            type: null
          }
          const codeResponse: any = await getCodes(requestVars);
          const statuses = codeResponse.bulkStatuses;
          statuses.unshift(allCodeObj);
          const dataTypes = codeResponse.bulkDataTypes;
          dataTypes.unshift(allCodeObj)
          const fileFormats = codeResponse.bulkFileFormats;
          fileFormats.unshift(allCodeObj)
          setFileStatuses(statuses);
          setDataTypes(dataTypes);
          setFileFormats(fileFormats);
        }
        catch (error) {
          errorResponse(error, addAlert, alertMessages.getFiles);
        }
      })();
    }
  }, [addAlert, setCallMadeCodes, callMadeCodes,allCodeObj, fileStatuses]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

    /* this will handle start date*/
    const handleStartDate = (newValue: any)=>{
      if( !isValidDate(newValue) ){
        setErrorStartDate('Invalid Date');
        return;
      }
      setStartDate(moment(newValue).utc().toDate());
      if((moment(newValue) <= moment(endDate))){
        if(errorEndDate=== 'Invalid Date' || (moment()<=moment(startDate)) || moment()<=moment(endDate) || newValue=== 'Invalid Date'){
          return
        }
        
        else{
          setErrorEndDate(null)
          setErrorStartDate(null)
        }
      }
      else if(moment(newValue) > moment(endDate)) {
        setErrorStartDate('Start Date is after End Date');
      }
      else{
        setErrorStartDate('Invalid Date');
      }
    }
  
    /* this will handle end date changed*/
    const handleEndDate = (newValue: any)=>{
      if( !isValidDate(newValue) ){
        setErrorEndDate('Invalid Date');
        return;
      }
      setEndDate(moment(newValue).utc().toDate());
      if((moment(newValue) >= moment(startDate))){
        if(errorStartDate=== 'Invalid Date' || (moment()<=moment(startDate)) || (moment()<=moment(endDate)) || newValue=== 'Invalid Date'){
          return
        }
        else{
          setErrorEndDate(null)
          setErrorStartDate(null)
        }
      }
      else if(moment(newValue) < moment(startDate)){
        setErrorEndDate('End Date is before Start Date');
      }
      else{
        setErrorEndDate('Invalid Date');
      }
    }
  
  
//selected might be where selection is placed
  return (
    <div style={{paddingBottom: '2.5%'}} className='table-page-container' data-testid={'table-page-container'}>
      <Modal //Modal to open the Site Modal
        className="smooth-scrollbar"
        open={openUploadModal}
        onClose={handleCloseUpload}
      >
        <div>
          <Button style={{ left: '68%', top: '17%', position: 'absolute', cursor: 'pointer' }} onKeyDown={(e) => {e.key==='Enter'&& setOpenUploadModal(false) }} onClick={() => { setOpenUploadModal(false) }}>
            <CloseIcon style={{ color: 'white' }} />
          </Button>
          <Box sx={modalUpload} className="smooth-scrollbar removePadding">
            <DataUpload token={token} open={openUploadModal} setOpen={setOpenUploadModal} handleRefresh={handleRefresh}/>
          </Box>
        </div>
      </Modal>
        <h1>Field Sample Data Upload</h1>
        <hr></hr>
          {/*DROP DOWNS AND FILTERS*/}
        <GridContainer className="margin-top-3 dropdown-style" style={{ paddingBottom:'2.5%'}}>
          <Grid row gap="md">
            <div style={{ width: '14%'}}>
              <label className="font-sans-md text-bold" htmlFor='filesSearch'>Search All Files</label>
            </div>
            <div style={{ width: '14%' }}>
                <label className="font-sans-md text-bold" htmlFor='uploadStartDate'>Upload Start Date</label>
            </div>
            <div style={{ width: '14%' }}>
                <label className="font-sans-md text-bold" htmlFor='uploadEndDate'>Upload End Date</label>
            </div>
            <div style={{ width: '12%' }}>
              <label className="font-sans-md text-bold" htmlFor='fileFormat'>File Format</label>
            </div>
            <div style={{ width: '12%' }}>
              <label className="font-sans-md text-bold" htmlFor='dataType'>Data Type</label>
            </div>
            <div style={{ width: '14%' }}>
              <label className="font-sans-md text-bold" htmlFor='status'>Status</label>
            </div>
            <div style={{ width: '18%' }}>
            </div>
          </Grid>
          <Grid row gap="md" className="margin-top-1">

          <div style={{ width: '14%' }}>
            <div className='searchFiles'>
              <i className="fas fa-search" />
              <Autocomplete
                onChange={(event: any, newValue: any) => {
                  setFileNameSearch(newValue);
                  setPage(0);
                }}
                inputValue={fileNameSearch? fileNameSearch : ''}
                onInputChange={(event, newInputValue) => {
                  setFileNameSearch(newInputValue);
                  setPage(0);
                }}
                aria-labelledby='filesSearch'
                id="filesSearch"
                options={fileNames?.length >= 1 ? (fileNames) : ([])}
                getOptionLabel={(option) => option.fileName}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search All Files"
                    InputProps={{
                      ...params.InputProps,
                      type: 'search',
                      style: { height: '5rem' },
                    }}
                    style={{ height: '3.5rem' }}

                  />
                )}
              />
            </div>
          </div>
          <div style={{ width: '14%' }}>
            <DatePicker
              disableFuture
              value={startDate}
              slotProps={{
                textField: {
                  error: errorStartDate ? true : false,
                  helperText: errorStartDate,
                  id: 'uploadStartDate',
                },

              }}
              onError={(reason) => {
                if (reason === 'disableFuture') {
                  setErrorStartDate('Start Date is in the Future');
                }
                else if (reason === 'invalidDate') {
                  setErrorStartDate('Invalid Date');
                }
                else if (reason === null && moment(startDate) <= moment(endDate)) {
                  setErrorStartDate(null);
                  setErrorEndDate(null);
                }
                else if (moment(startDate) > moment(endDate)) {
                  setErrorStartDate('Start Date is after End Date');
                }
                else {
                  setErrorStartDate('An Error on Start Date')
                }
              }}
              onChange={(newValue) => { 
                handleStartDate(newValue);
                setPage(0);
              }}
            />
          </div>
          <div style={{ width: '14%' }}>
            <DatePicker
              value={endDate}
              disableFuture
              aria-labelledby='uploadEndDate'

              onError={(reason) => {
                if (reason === 'disableFuture') {
                  setErrorEndDate('End Date is in the Future');
                }
                else if (reason === 'invalidDate') {
                  setErrorEndDate('Invalid Date');
                }
                else if (reason === null && moment(startDate) <= moment(endDate)) {
                  setErrorStartDate(null);
                  setErrorEndDate(null);
                }
                else if (moment(startDate) > moment(endDate)) {
                  setErrorEndDate('End Date is before End Date');
                }
                else {
                  setErrorStartDate('An Error on Start Date')
                }
              }}
              slotProps={{
                textField: {
                  error: errorEndDate ? true : false,
                  helperText: errorEndDate,
                  id: 'uploadEndDate',

                },
              }}
              onChange={(newValue) => {
                handleEndDate(newValue);
                setPage(0);
              }}
            />
          </div>
          <div style={{ width: '12%' }}>
            <Autocomplete
              className="height-55"
              value={format}
              onChange={(event: any, newValue: any) => {
                setFormat(newValue);
                setPage(0);
              }}
              inputValue={inputFormat}
              onInputChange={(event, newInputValue) => {
                setInputFormat(newInputValue);
                setPage(0);
              }}
              disableClearable
              id="fileFormat"
              options={(fileFormats.map((option) => option.code_key))}
              aria-labelledby='fileFormat'
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'fileFormat'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { visibility: 'hidden' }
                  }}
                />
              )}
            />
          </div>
          <div style={{ width: '12%' }}>
            <Autocomplete
              className="height-55"
              value={dataType}
              onChange={(event: any, newValue: any) => {
                setDataType(newValue);
                setPage(0);
              }}
              inputValue={inputDataType}
              onInputChange={(event, newInputValue) => {
                setInputDataType(newInputValue);
                setPage(0);
              }}
              disableClearable
              aria-labelledby='dataType'
              id="dataType"
              options={dataTypes.map((option) => option?.code_key)}
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'dataType'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { visibility: 'hidden' }
                  }}
                />
              )}
            />
          </div>
          <div style={{ width: '14%' }}>
            <Autocomplete
              className="height-55"
              value={status}
              onChange={(event: any, newValue: any) => {
                setStatus(newValue);
                setPage(0);
              }}
              inputValue={statusInput}
              onInputChange={(event, newInputValue) => {
                setStatusInput(newInputValue);
                setPage(0);
              }}
              disableClearable
              id="status"
              aria-labelledby='status'
              options={fileStatuses.map((option) => option?.code_key)}
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'status'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { visibility: 'hidden' }
                  }}
                />
              )}
            />
          </div>
          <div style={{ width: '14%' }}>
            <Tooltip title={"Upload Data"} placement="top-start">
              <span>
                <Button variant="outlined" style={{height: '90%'}} onClick={handleOpenUpload}> <UploadIcon/> Upload Data</Button>
              </span>
            </Tooltip>
          </div>
          <div style={{ width: '2%' }}>
            <Tooltip title={"Refresh Table"} placement="top-start">
                <Button variant="outlined" style={{height: '90%'}} onClick={handleRefresh}><RefreshIcon/></Button>
            </Tooltip>
          </div>

          </Grid>
        </GridContainer>

        <TableContainer component={Paper} className='margin-top-2 first-table MuiPaper-root-special'>
        {(loading) && (
          <div style={{height: '30%'}}>
          <LoadingSpinner/>
          </div>
        )}
        {/* TABLE FOR FUELS*/}
          <Table
            sx={{ minWidth: 750 }}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
            />
            <TableBody>
              {files.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <StyledTableRow
                    tabIndex={-1}
                    key={row.jobId}
                    className='selectedHover'
                  >
                    <StyledTableCell className='text-align-override selectedHover'
                      component="th"
                      id={labelId}
                      scope="row"
                      tabIndex={0}
                      padding="none"
                    >
                      <a href={row.originalFileLink}>{row.fileName}</a>
                    </StyledTableCell>
                    <StyledTableCell className='text-align-override' 
                      component="td"
                      tabIndex={0}
                    >
                      {row.createdTime}
                    </StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}

                    >{row.jobCompletionTime}</StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.jobType}</StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.size}</StyledTableCell>
                    {(row.jobStatus.indexOf('Complete')>-1) &&
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    ><a href={row.reportLink}>{row.jobStatus}</a></StyledTableCell>}
                    {(row.jobStatus.indexOf('Complete')===-1) &&
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.jobStatus}</StyledTableCell>}
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.createdBy}</StyledTableCell>
                  </StyledTableRow>
                );//
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 30]}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />

    </div>
  );
}